import { useToast } from "@app/components/toast";
import { environment } from "@app/relay/relay-environment";
import * as Dialog from "@radix-ui/react-dialog";
import { Fragment, Suspense, useState } from "react";
import { commitMutation, graphql, useLazyLoadQuery } from "react-relay";

import type { walletManageFilesDialog_Query } from "@app/queries/walletManageFilesDialog_Query.graphql";
import type { walletManageFilesDialog_ClearMutation } from "@app/queries/walletManageFilesDialog_ClearMutation.graphql";

const manifestQuery = graphql`
  query walletManageFilesDialog_Query($slug: String!) {
    walletFileManifest(slug: $slug) {
      files {
        filename
        uploadedAt
        rows
        firstDate
        lastDate
        duplicate
        provider
        accountNumber
        accountKind
        notice
      }
      outflowMonths {
        month
        count
      }
    }
  }
`;

const clearMutation = graphql`
  mutation walletManageFilesDialog_ClearMutation($slug: String!) {
    walletUploadsClear(slug: $slug) {
      cleared
      errors {
        message
      }
    }
  }
`;

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const YEARS_BACK = 10;

export default function WalletManageFilesDialog({
  slug,
  name,
  onClose,
}: {
  slug: string;
  name: string;
  onClose: () => void;
}) {
  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-[min(60rem,92vw)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-lg bg-white shadow-xl">
          <div className="flex items-center justify-between border-b px-5 py-4">
            <Dialog.Title className="font-semibold text-gray-900">
              {name} files
            </Dialog.Title>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
          <Suspense
            fallback={<div className="p-6 text-sm text-gray-400">Loading files…</div>}
          >
            <Body slug={slug} name={name} />
          </Suspense>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Body({ slug, name }: { slug: string; name: string }) {
  const notify = useToast();
  const data = useLazyLoadQuery<walletManageFilesDialog_Query>(
    manifestQuery,
    { slug },
    { fetchPolicy: "store-or-network" }
  );
  const files = data.walletFileManifest.files;
  const months = data.walletFileManifest.outflowMonths;
  const [clearing, setClearing] = useState(false);

  const clearAll = () => {
    if (
      !window.confirm(
        `Remove all uploaded ${name} files? The staged data is deleted; you can re-upload afterwards.`
      )
    ) {
      return;
    }
    setClearing(true);
    commitMutation<walletManageFilesDialog_ClearMutation>(environment, {
      mutation: clearMutation,
      variables: { slug },
      onCompleted: (res) => {
        setClearing(false);
        if (res.walletUploadsClear?.errors?.length) {
          notify({ message: res.walletUploadsClear.errors[0].message, kind: "error" });
          return;
        }
        window.location.reload();
      },
      onError: (e) => {
        setClearing(false);
        notify({ message: e.message, kind: "error" });
      },
    });
  };

  return (
    <div className="flex-1 space-y-6 overflow-auto p-5">
      {/* 10-year outflow-count grid */}
      <section>
        <h3 className="mb-1 text-xs font-medium uppercase text-gray-500">
          Outflows by month
        </h3>
        <MonthGrid months={months} />
      </section>

      {/* Files table */}
      <section>
        <h3 className="mb-1 text-xs font-medium uppercase text-gray-500">
          Uploaded files ({files.length})
        </h3>
        {files.length === 0 ? (
          <p className="text-sm text-gray-400">No files uploaded yet.</p>
        ) : (
          <div className="overflow-x-auto rounded border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase text-gray-400">
                  <th className="px-3 py-2">File</th>
                  <th className="px-3 py-2">Uploaded</th>
                  <th className="px-3 py-2 text-right">Rows</th>
                  <th className="px-3 py-2">Covers</th>
                  {/* What the file was read AS and where its rows went. Without
                      these two columns a wallet with files and no balance cannot be
                      diagnosed from the screen at all. */}
                  <th className="px-3 py-2">Read as</th>
                  <th className="px-3 py-2">Landed in</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {files.map((f, i) => (
                  <Fragment key={`${f.filename ?? "file"}-${i}`}>
                    <tr className={f.notice ? "" : "border-b last:border-b-0"}>
                      <td className="px-3 py-1.5">{f.filename ?? "—"}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-gray-500">
                        {f.uploadedAt ? new Date(f.uploadedAt).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {f.rows.toLocaleString()}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-gray-500">
                        {f.firstDate && f.lastDate ? `${f.firstDate} → ${f.lastDate}` : "—"}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap font-mono text-xs text-gray-500">
                        {f.provider ?? "—"}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-gray-500">
                        {f.accountNumber ? (
                          <>
                            {f.accountNumber}
                            {f.accountKind && (
                              <span
                                className={`ml-1.5 rounded px-1.5 py-0.5 text-xs ${
                                  f.accountKind === "WALLET"
                                    ? "bg-emerald-100 text-emerald-800"
                                    : "bg-gray-100 text-gray-600"
                                }`}
                              >
                                {f.accountKind}
                              </span>
                            )}
                          </>
                        ) : (
                          "not promoted"
                        )}
                      </td>
                      <td className="px-3 py-1.5">
                        {f.duplicate && (
                          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                            duplicate
                          </span>
                        )}
                      </td>
                    </tr>
                    {/* What the parse dropped or repaired to make this file
                        importable. A wallet upload only stages, so this is the one
                        place the rows a widened export window added are visible. */}
                    {f.notice && (
                      <tr className="border-b last:border-b-0">
                        <td
                          colSpan={7}
                          className="px-3 pb-2 text-xs whitespace-pre-line text-amber-700"
                        >
                          {f.notice}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {files.length > 0 && (
        <div className="flex justify-end border-t pt-4">
          <button
            type="button"
            onClick={clearAll}
            disabled={clearing}
            className="rounded border border-red-200 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
          >
            {clearing ? "Clearing…" : "Clear all files"}
          </button>
        </div>
      )}
    </div>
  );
}

// Rows = the last 10 calendar years, columns = Jan–Dec, each cell the outflow-row
// count for that month (blank when zero). Months stay in fixed columns across years.
function MonthGrid({ months }: { months: readonly { month: string; count: number }[] }) {
  const counts = new Map(months.map((m) => [m.month, m.count]));
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: YEARS_BACK }, (_, i) => currentYear - i);
  const yearTotal = (year: number) =>
    Array.from({ length: 12 }, (_, m) => counts.get(`${year}-${String(m + 1).padStart(2, "0")}`) ?? 0)
      .reduce((a, b) => a + b, 0);

  return (
    <div className="overflow-x-auto rounded border">
      <table className="min-w-[720px] w-full text-sm">
        <thead>
          <tr className="border-b text-xs uppercase text-gray-400">
            <th className="px-3 py-2 text-left font-medium">Year</th>
            {MONTH_LABELS.map((label) => (
              <th key={label} className="px-2 py-2 text-right font-medium">
                {label}
              </th>
            ))}
            <th className="px-3 py-2 text-right font-medium text-gray-500">Total</th>
          </tr>
        </thead>
        <tbody>
          {years.map((year) => {
            const total = yearTotal(year);
            return (
              <tr key={year} className="border-b last:border-b-0">
                <td className="px-3 py-1.5 font-medium text-gray-700">{year}</td>
                {MONTH_LABELS.map((_, m) => {
                  const count = counts.get(`${year}-${String(m + 1).padStart(2, "0")}`) ?? 0;
                  return (
                    <td key={m} className="px-2 py-1.5 text-right tabular-nums">
                      {count > 0 ? count : <span className="text-gray-200">·</span>}
                    </td>
                  );
                })}
                <td className="px-3 py-1.5 text-right font-semibold tabular-nums text-gray-600">
                  {total > 0 ? total : <span className="text-gray-300">—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
